import { Button } from '@creditornot/cb-button';
import { ImageStack, Plus, Upload } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParam, StringParam } from 'use-query-params';

import { useMenuActionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useWoltSpecials } from 'modules/shared-menu/pages/SharedMenuEditorPage/context/WoltSpecialsContext';

type Action = {
  icon: React.ReactNode;
  name: string;
  onClick: () => void;
};

type ActionGroup = {
  title: string;
  actions: Action[];
};

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px;
`;

const Download = styled(Upload)`
  transform: rotate(180deg);
`;

const ActionGroup = styled(MenuItemGroup)`
  & + & {
    margin-block-start: 16px;
  }
`;

function isTruthy<T>(argument: T | boolean | undefined): argument is T {
  return !!argument;
}

export const CentralisedAddButton = () => {
  const intl = useIntl();
  const history = useHistory();
  const { menuEditorBulkImageUploadPage, menuEditorGoogleSheetsImportPage, menuEditorAddCategoriesPage } =
    useListingManagerRoutes();
  const { woltSpecialsCategory } = useWoltSpecials();
  const selectionHasWoltSpecials = !!woltSpecialsCategory;
  const menuPermissions = useOfferingMenuPermissionsContext();
  const [, setActiveItemId] = useQueryParam('item', StringParam);
  const [, setActiveOptionId] = useQueryParam('option', StringParam);
  const { trackImportImages } = useMenuActionsTracking();

  const handleCreateOfferClick = useCallback(() => {
    setActiveOptionId(null);
    setActiveItemId('new-item');
  }, [setActiveItemId, setActiveOptionId]);

  const handleCreateSpecialClick = useCallback(() => {
    setActiveOptionId(null);
    setActiveItemId('new-special');
  }, [setActiveItemId, setActiveOptionId]);

  const handleCreateOptionClick = useCallback(() => {
    setActiveOptionId('new-option');
    setActiveItemId(null);
  }, [setActiveItemId, setActiveOptionId]);

  const actionGroups: ActionGroup[] = useMemo(
    () =>
      [
        {
          title: intl.formatMessage({ id: 'shared-menu.editor.centralised-actions.create.title' }),
          actions: [
            {
              icon: <Plus />,
              name: intl.formatMessage({ id: 'shared-menu.editor.centralised-actions.create.offer' }),
              onClick: handleCreateOfferClick,
            },
            selectionHasWoltSpecials && {
              icon: <Plus />,
              name: intl.formatMessage({ id: 'shared-menu.editor.centralised-actions.create.special' }),
              onClick: handleCreateSpecialClick,
            },
            {
              icon: <Plus />,
              name: intl.formatMessage({ id: 'shared-menu.editor.centralised-actions.create.option' }),
              onClick: handleCreateOptionClick,
            },
            {
              icon: <Plus />,
              name: intl.formatMessage({ id: 'menu.bulkCreateCategories.addCategory.title' }),
              onClick: () => history.push(menuEditorAddCategoriesPage()),
            },
          ].filter(isTruthy),
        },
        menuPermissions.contentEdit && {
          title: intl.formatMessage({ id: 'shared-menu.editor.centralised-actions.bulk-update.title' }),
          actions: [
            {
              icon: <Upload />,
              name: intl.formatMessage({
                id: 'shared-menu.editor.centralised-actions.bulk-update.google-sheets-import',
              }),
              onClick: () => history.push(menuEditorGoogleSheetsImportPage()),
            },
            {
              icon: <Download />,
              name: intl.formatMessage({
                id: 'shared-menu.editor.centralised-actions.bulk-update.google-sheets-export',
              }),
              onClick: () => history.push(`${menuEditorGoogleSheetsImportPage()}?export=true`),
            },
            {
              icon: <Download />,
              name: intl.formatMessage({
                id: 'shared-menu.editor.centralised-actions.bulk-update.google-sheets-export-sample',
              }),
              onClick: () => history.push(`${menuEditorGoogleSheetsImportPage()}?downloadSample=true`),
            },
          ],
        },
        {
          title: intl.formatMessage({ id: 'menu.actions.menu.title' }),
          actions: [
            {
              icon: <ImageStack />,
              onClick: () => {
                trackImportImages();
                history.push(menuEditorBulkImageUploadPage());
              },
              name: intl.formatMessage({ id: 'menu.actions.menu.import-images' }),
            },
          ].filter(isTruthy),
        },
      ].filter(isTruthy),
    [
      intl,
      handleCreateOfferClick,
      selectionHasWoltSpecials,
      handleCreateSpecialClick,
      handleCreateOptionClick,
      history,
      menuEditorAddCategoriesPage,
      menuEditorGoogleSheetsImportPage,
      menuEditorBulkImageUploadPage,
      menuPermissions,
      trackImportImages,
    ],
  );

  return (
    <LegacyPopover
      placement="bottom-start"
      renderArrow={false}
      showOnHover={false}
      closeOnClickInside
      content={
        <PopoverContent>
          {actionGroups.map(group => (
            <ActionGroup key={group.title} title={group.title}>
              {group.actions.map(action => (
                <MenuActionItem key={action.name} icon={action.icon} onClick={action.onClick} variant="black">
                  {action.name}
                </MenuActionItem>
              ))}
            </ActionGroup>
          ))}
        </PopoverContent>
      }
    >
      <Button variant="primary" icon={<Plus />} size="small">
        <FormattedMessage id="shared-menu.editor.centralised-actions.button-label" />
      </Button>
    </LegacyPopover>
  );
};
